import React, { useEffect, useState } from "react";
import PageHeader from "../../../../components/common/PageHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../../../api/api";

import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import InquiryTable from "../components/InquiryTable";
import { VERTICALS } from "../../../../constants/verticals";
import { toast } from "react-toastify";
import { getClientSegmentMaster } from "../../../client/api/clientService";
import { getClientSegmentByVertical } from "../api/inquiry.api";
import { getDashboardCounts, getUserVerticalCounts } from "../../../dashboard/api/dashboardService";
import { useAuth } from "../../../../context/AuthContext";

const InquiryList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { vertical } = useParams<{ vertical: string }>();
  const location = useLocation();

  useEffect(() => {
  const params = new URLSearchParams(location.search);

  setFilters(prev => ({
    ...prev,
    activity: params.get("activity") || "",
    stage: params.get("stage") || "",
    probability: params.get("probability") || ""
  }));

  setPage(1);

}, [location.search]);

  const verticalConfig = VERTICALS.find(v => v.key === vertical);

  const verticalId = verticalConfig?.id;
  const verticalName = verticalConfig?.label;

  const [verticalSummary, setVerticalSummary] = useState<any>(null);

  /* ================= STATE ================= */
  const [list, setList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [ordering, setOrdering] = useState("");
  const [clientSegmentOptions, setClientSegmentOptions] = useState<
    { value: number; label: string }[]
  >([]);


  const [filters, setFilters] = useState<{
    client_name: string;
    contact: string;
    client_segment: number | null;
    stage: string;
    status: string;
    quotation: string;
    assigned_to: string;
    probability: string;
    rfq: string;
    architect: string;
    latest_activity: string;
    scheduled_activity:string;
    activity:string;
  }>({
    client_name: "",
    contact: "",
    client_segment: null, // ✅ VALUE
      stage: "",
    status: "",
  quotation: location.state?.quotation || "",
    assigned_to: "",
  probability: "",
    rfq: location.state?.rfq || "",
    architect: "",
    latest_activity: "",
    scheduled_activity:"",
      activity: ""   // ✅ ADD THIS
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  /* ================= SORT HELPERS ================= */
  const getSortIcon = (field: string) => {
    if (ordering === field) return faSortUp;
    if (ordering === `-${field}`) return faSortDown;
    return faSort;
  };

  const toggleOrdering = (field: string) => {
    setOrdering((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return "";
      return field;
    });
  };

  /* ================= API ================= */
  const fetchInquiries = async (
    pageNo: number,
    appliedFilters = debouncedFilters
  ) => {
    if (!verticalId) return;

    const res = await api.get("/crm/v1/glazing-enquiry/", {
      params: {
        page: pageNo,
        vertical: verticalId,   // ✅ THIS WAS MISSING
        ordering,
        ...appliedFilters,
      },
    });

    setList(res.data.results || []);
    setTotalCount(res.data.count || 0);

    if (pageSize === null && res.data.results?.length) {
      setPageSize(res.data.results.length);
    }
  };

  // debounce filters
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 500);

    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    const loadVerticalSummary = async () => {
      try {
        if (!verticalId) return;

        let res;

        /* ================= ADMIN ================= */
        if (user?.role?.toLowerCase() === "admin") {
          res = await getDashboardCounts();

          const verticals = res.data?.verticals || {};

          const v =
            Object.values(verticals).find(
              (x: any) => x.vertical_id === verticalId
            ) || null;

          setVerticalSummary(v);
          return;
        }


        /* ================= NON-ADMIN ================= */
        if (!user?.user_id) return;

        res = await getUserVerticalCounts({
          owner_id: user.user_id,
          vertical: verticalId,
        });

        const v = Object.values(res.data?.verticals || {})[0] || null;
        setVerticalSummary(v);

      } catch (err) {
        console.error("Vertical summary load failed", err);
      }
    };

    loadVerticalSummary();
  }, [verticalId, user]);



  // single fetch source
  useEffect(() => {
    fetchInquiries(page, debouncedFilters);
  }, [page, ordering, debouncedFilters]);

  /* ================= PAGINATION ================= */
  const totalPages =
    pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;
  useEffect(() => {
    if (!verticalId) return;

    getClientSegmentByVertical(verticalId)
      .then((res) => {
        setClientSegmentOptions(
          res.data.results.map((s: any) => ({
            value: s.id,
            label: s.name, // no need to show vertical name now
          }))
        );
      })
      .catch(() => {
        toast.error("Failed to load client segments");
      });
  }, [verticalId]);

  return (
    <>
      <PageHeader
        title={`${verticalName} – Sales Inquiry`}

        breadcrumbs={[
          { label: "Home" },
          { label: "Sales" },
          { label: verticalName || "" },

        ]}
      />

      <div className="container mt-4">


        <div className="info-card">
          {verticalSummary && (
            <div className="info-grid mb-3 vertical-info-grid">

              <InfoBox color="blue" icon="fa-users" label="Inquiries" value={verticalSummary.inquiries} />
              <InfoBox color="green" icon="fa-chart-line" label="Inquiries Won" value={verticalSummary.inquiries_won} />
              <InfoBox color="yellow" icon="fa-star" label="High Probability" value={verticalSummary.high_probability} />
              <InfoBox color="purple" icon="fa-clock" label="RFQ Pending" value={verticalSummary.rfq_pending} />
              <InfoBox color="orange" icon="fa-file-invoice" label="Quotation Pending" value={verticalSummary.quotation_pending} />
              <InfoBox color="red" icon="fa-triangle-exclamation" label="Lost Inquiries" value={verticalSummary.lost_enquiries} />

            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0"> {`${verticalName} – Inquiry List`}</h4>

            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${vertical}/inquiries/create`)}

            >
              <i className="fas fa-plus me-2"></i>
              Create Inquiry
            </button>
          </div>




          {/* ================= TABLE ================= */}
          <InquiryTable
            data={list}
            filters={filters}
            setFilters={setFilters}
            toggleOrdering={toggleOrdering}
            getSortIcon={getSortIcon}
            clientSegmentOptions={clientSegmentOptions}
            vertical={vertical!}
          />

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end gap-1 mt-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`btn btn-sm ${p === page
                    ? "btn-primary"
                    : "btn-outline-secondary"
                    }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InquiryList;

const InfoBox = ({ color, icon, label, value }: any) => (
  <div className={`info-box ${color}`}>
    <i className={`fas ${icon}`}></i>
    <strong>{value || 0}</strong>
    <span>{label}</span>
  </div>
);
